<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.detailCompletionTime')"
        no-reset
        size="medium"
        @close="close">
        <hts-card :title="$t('labels.taskInformation')">
            <hts-admincp-select-node
                v-model="data.node"
                :text="data.nodeName"
                readonly
                key-id="_id"
                key-label="name"
                :clearable="true"
                @change="(val) => changeNodeSelected(val)" />

            <hts-select-task
                v-model="data.task"
                :text="data.taskInfo.name"
                :items="generalTasks"
                :label="$t('labels.task')"
                readonly
                @change="(val) => changeTaskSelected(val)">
                <template #footer="{ items, getBusinessName }">
                    <div
                        v-for="(item, index) in items"
                        :key="index">
                        <p
                            v-if="Array.isArray(items) && items.length"
                            class="py-2 px-1">
                            <span>
                                {{ $t('labels.business') }}:
                            </span>
                            <template v-if="item.businesses && Array.isArray(item.businesses) && item.businesses.length">
                                <span
                                    v-for="(business, indexBusinesses) in item.businesses"
                                    :key="indexBusinesses"
                                    class="text-sm px-2 py-1 mx-1 border rounded text-gray-500">
                                    {{ getBusinessName(business) }}
                                </span>
                            </template>
                        </p>
                    </div>
                </template>
            </hts-select-task>
        </hts-card>

        <hts-card :title="$t('labels.completionTime')">
            <hts-form-checkbox
                v-model="data.completionTimeCategories"
                :items="filterOptions"
                readonly />

            <div class="overflow-x-auto">
                <div
                    :style="{ minWidth: minWidthScroll }"
                    class="pb-2 grid-cols-1 grid gap-2">
                    <div class="grid-cols-2 grid gap-x-2">
                        <p class="mb-2">
                            {{ $t('labels.type') }}
                        </p>
                        <p>
                            {{ $t('labels.averageCompletionTime') }} <span class="text-error-500">
                                *
                            </span>
                        </p>
                    </div>
                    <template v-if="data.completionTimeCategories.includes(0)">
                        <div
                            v-for="completionTimeVehicle in data.completionTimes.vehicleCategory"
                            :key="completionTimeVehicle"
                            class="grid-cols-2 grid gap-x-5">
                            <hts-form-input
                                v-model="completionTimeVehicle.name"
                                readonly />
                            <hts-form-input
                                v-model="completionTimeVehicle.averageTime"
                                type="number"
                                required
                                :number-decimal="1"
                                readonly />
                        </div>
                    </template>
                    <template v-if="data.completionTimeCategories.includes(1)">
                        <div
                            v-for="completionTimeCont in data.completionTimes.contCategory"
                            :key="completionTimeCont"
                            class="grid-cols-2 grid gap-x-5">
                            <hts-form-input
                                v-model="completionTimeCont.name"
                                readonly />
                            <hts-form-input
                                v-model="completionTimeCont.averageTime"
                                type="number"
                                required
                                :number-decimal="1"
                                readonly />
                        </div>
                    </template>
                </div>
            </div>
        </hts-card>
    </hts-drawer>
</template>

<script lang="ts" setup>
import { IEditTaskCompletionTime } from './form'
import { combineKeyValue } from './utils'

import screen from '~/components/utils/screen'
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})

const emits = defineEmits(['refreshTables'])

const store = $store()
const visible = ref(false)
const cooperatorId = computed(() => useRoute().params.idpartner)
const data = ref<IEditTaskCompletionTime>({
  node: '',
  nodeName: '',
  task: '',
  taskInfo: {
    key: '',
    business: '',
    task: '',
    name: ''
  },
  completionTimes: {
    vehicleCategory: [
      {
        id: '',
        name: '',
        averageTime: null
      }
    ],
    contCategory: [
      {
        id: '',
        name: '',
        averageTime: null
      }
    ]
  },
  completionTimeCategories: []
})
const nodeSelected = ref<{ [key: string]: string }[]>([])
const filterType = ref([0, 1])
const filterOptions = ref([
  {
    value: 0,
    label: $t('labels.vehicleType')
  },
  {
    value: 1,
    label: $t('labels.container')
  }
])
const completionTimeId = ref<string>('')
const taskData = computed(() => {
  return store.data.getTasks as any
})

const open = async (id: string) => {
  completionTimeId.value = id
  await getData(id)
  visible.value = true
}

const getData = async (id: string) => {
  if (!id) {
    return
  }

  const api = $urlClient.partner.taskCompletionTime.getById
  const dataR: any = await $api(api, {
    uriParams: {
      ID: id,
      cooperatorId: cooperatorId.value
    }
  })
  const resData = dataR?.data?.value

  if (resData?.success) {
    data.value.node = resData.data?.node?._id
    nodeSelected.value = [resData.data.node]
    data.value.nodeName = `${resData.data.node?.nodeType?.name}-${resData.data?.node?.location?.locationName}` || resData.data.nodeName
    data.value.task = `${resData.data.taskInfo.business}-${resData.data?.taskInfo?.task}`
    data.value.taskInfo = {
      key: `${resData.data.taskInfo.business}-${resData.data?.taskInfo?.task}`,
      business: resData.data.taskInfo.business,
      task: resData.data.taskInfo.task,
      name: resData.data.taskInfo.taskName
    }

    const task = taskData.value.find((task: any) => resData.data.taskInfo.task === task._id)
    if (task) {
      data.value.completionTimeCategories = task.completionTimeCategories || []
    }
    await setDataEstimatedCompletionTime(resData.data.completionTimes)
  }
}

const close = () => {
  visible.value = false
}

const changeNodeSelected = (val: { [key: string]: string }[]) => {
  nodeSelected.value = val
  data.value.task = ''
  data.value.taskInfo = {
    key: '',
    business: '',
    task: '',
    name: ''
  }
}

const changeTaskSelected = (val: { [key: string]: string }[]) => {
  if (Array.isArray(val) && val.length) {
    data.value.taskInfo = val[0] as any
  } else {
    data.value.taskInfo = {
      key: '',
      business: '',
      task: '',
      name: ''
    }
  }
}

const generalTasks = computed(() => {
  const businesses: any = nodeSelected.value.map((item: { [key: string]: string }) => {
    return item.businesses || []
  })

  if (!Array.isArray(businesses) || businesses.length === 0) {
    return []
  }

  const businessTaskIds = combineKeyValue(businesses) as { [key: string]: any }

  const results: {
    _id: string
    key: string
    name: string
    business: string
    businesses: string[]
    task: string
    completionTimeCategories: number[]
  }[] = []

  Object.keys(businessTaskIds).forEach((businessId: string) => {
    const taskIds = businessTaskIds[businessId] || []
    taskIds.forEach((taskId: string) => {
      const key = `${businessId}-${taskId}`
      const task = taskData.value.find((task: any) => taskId === task._id)

      if (!task) {
        return
      }
      const exists = results.find((e: any) => e._id === taskId)
      if (!exists) {
        const { name, completionTimeCategories } = task
        results.push({
          _id: key,
          key,
          business: businessId,
          businesses: [businessId],
          task: taskId,
          name,
          completionTimeCategories
        })
      }
    })
  })

  results.sort((a, b) => {
    const taskNameA = a.name.toLowerCase()
    const taskNameB = b.name.toLowerCase()

    if (taskNameA < taskNameB) {
      return -1
    } else if (taskNameA > taskNameB) {
      return 1
    } else {
      return 0
    }
  })

  return results
})

const setDataEstimatedCompletionTime = (completionTimes: any) => {
  if (!completionTimes) {
    data.value.completionTimes = {
      vehicleCategory: [
        {
          id: '',
          name: '',
          averageTime: null
        }
      ],
      contCategory: [
        {
          id: '',
          name: '',
          averageTime: null
        }
      ]
    }
    return
  }

  const completionTimeCont: {
    id: string | number
    name: string
    averageTime: number | null
  }[] = []

  let completionTimeVehicle: {
    id: string
    name: string
    averageTime: number | null
  }[] = []

  const completionTimesValue = {
    contCategory: completionTimeCont,
    vehicleCategory: completionTimeVehicle
  }

  contTypes.forEach((item: { [key: string]: string | number }) => {
    if (completionTimes?.contCategory?.[item.value]) {
      completionTimeCont.push(completionTimes?.contCategory[item.value])
    } else {
      completionTimeCont.push({
        id: item.value as number,
        name: item.label as string,
        averageTime: null
      })
    }
  })
  completionTimesValue.contCategory = completionTimeCont

  if (completionTimes.vehicleCategory) {
    completionTimeVehicle = Object.values(completionTimes.vehicleCategory)
  } else {
    completionTimeVehicle = [
      {
        id: '',
        name: '',
        averageTime: null
      }
    ]
  }

  completionTimesValue.vehicleCategory = completionTimeVehicle

  data.value.completionTimes = completionTimesValue
}

const rules = {
  node: 'required',
  task: 'required'
}

const contTypes = [
  {
    value: 20,
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: 40,
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: 45,
    label: $t('labels.vehicleModule.45ft')
  }
]

defineExpose({
  open,
  close
})
</script>
